import { getApiServerConfig } from '@/utils/serverConfig';
import { getActiveDomain } from '@/utils/IMEI';
import { isDev } from '@/config';
import DevConfig from '@/config/env/dev.json';
import ProdConfig from '@/config/env/prod.json';

// sessionStorage 缓存 key(关标签页即失效，刷新可复用)
const CACHE_KEY = 'serverConfigCache';
const ACTIVITY_DOMAIN_KEY = 'activityDomain';

const isProduction = !isDev;

// 本地/测试用 dev.json，生产用 prod.json 兜底
const DEFAULT_SERVICE_CONFIG = isProduction
  ? ProdConfig.SERVICE_CONFIG
  : DevConfig.SERVICE_CONFIG;

function readCache() {
  try {
    const str = sessionStorage.getItem(CACHE_KEY);
    if (!str) { return {} }
    const data = JSON.parse(str);
    return data && typeof data === 'object' ? data : {};
  } catch (e) {
    return {};
  }
}

function writeCache(config) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(config));
  } catch (e) {
    // ignore
  }
}

// 远程返回可能是 { SERVICE_CONFIG: {...} }，也可能直接是配置对象
function pickServiceConfig(remote) {
  if (!remote || typeof remote !== 'object') { return null }
  const config = remote.SERVICE_CONFIG || remote;
  return config && typeof config === 'object' ? config : null;
}

// 并发调用复用同一个 Promise
let initPromise = null;
let domainPromise = null;

const state = {
  // 任何时刻取配置都不会是空对象
  serviceConfig: Object.assign({}, DEFAULT_SERVICE_CONFIG, readCache()),
  activityDomain: sessionStorage.getItem(ACTIVITY_DOMAIN_KEY) || '',
  ready: false,
};

const getters = {
  // 通用取值：getters['serverConfig/serviceUrl']('PEMapi')
  serviceUrl: (state) => (key, index = 0) => {
    const value = state.serviceConfig[key];
    if (!value) { return '' }
    if (Array.isArray(value)) { return value[index] || '' }
    return value;
  },
  // 活动站点域名列表(已过滤空串)
  activityUrls: (state) => {
    const urls = state.serviceConfig.ActivityUrl;
    if (!urls) { return [] }
    return (Array.isArray(urls) ? urls : [urls]).filter((item) => !!item);
  },
};

const mutations = {
  SET_SERVICE_CONFIG(state, config) {
    state.serviceConfig = Object.assign({}, DEFAULT_SERVICE_CONFIG, config);
  },
  SET_ACTIVITY_DOMAIN(state, domain) {
    state.activityDomain = domain || '';
  },
  SET_READY(state, ready) {
    state.ready = !!ready;
  },
};

const actions = {
  // 入口调用：非生产不请求配置接口，生产拉一次远程配置(失败不阻塞启动)
  initServerConfig({ commit, state }, force = false) {
    if (!isProduction) {
      commit('SET_READY', true);
      return Promise.resolve(state.serviceConfig);
    }
    if (initPromise && !force) { return initPromise }
    initPromise = (async () => {
      try {
        const remote = await getApiServerConfig();
        const config = pickServiceConfig(remote);
        if (config) {
          commit('SET_SERVICE_CONFIG', config);
          writeCache(config);
          // 域名清单可能换了，清掉已探测的活动域名
          commit('SET_ACTIVITY_DOMAIN', '');
          sessionStorage.removeItem(ACTIVITY_DOMAIN_KEY);
        }
      } catch (e) {
        console.warn('[serverConfig] 初始化失败，使用本地默认配置', e && e.message);
      }
      commit('SET_READY', true);
      return state.serviceConfig;
    })();
    return initPromise;
  },

  // 轮循 ActivityUrl 取可访问域名，一个会话只探测一次
  getActivityDomain({ commit, state, getters }, force = false) {
    if (state.activityDomain && !force) {
      return Promise.resolve(state.activityDomain);
    }
    if (domainPromise && !force) { return domainPromise }
    domainPromise = (async () => {
      const urls = getters.activityUrls;
      if (!urls.length) { return '' }
      let domain = '';
      try {
        // getActiveDomain 入参是逗号拼接的字符串，内部 HEAD ${url}/ping 探测
        domain = await getActiveDomain(urls.join(','));
      } catch (e) {
        domain = '';
      }
      // 全部不可达时回退第一个域名
      if (!domain) { domain = urls[0] }
      commit('SET_ACTIVITY_DOMAIN', domain);
      try {
        sessionStorage.setItem(ACTIVITY_DOMAIN_KEY, domain);
      } catch (e) {
        // ignore
      }
      return domain;
    })();
    return domainPromise;
  },

  // 运行期域名被封时重拉配置
  reloadServerConfig({ dispatch }) {
    return dispatch('initServerConfig', true);
  },

  // 清远程缓存 + 活动域名缓存，回到默认配置
  clearServerConfig({ commit }) {
    initPromise = null;
    domainPromise = null;
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(ACTIVITY_DOMAIN_KEY);
    commit('SET_SERVICE_CONFIG', {});
    commit('SET_ACTIVITY_DOMAIN', '');
    commit('SET_READY', false);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
